import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { ChevronRight, ChevronLeft, X, Eye, Save, Zap, Plus, Check, Link2, Layers, LayoutGrid } from 'lucide-react';
import ImageUpload from './ImageUpload';
import CardPositioner from './CardPositioner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LANGS = ['en', 'fr', 'es'];
const LANG_LABELS = { en: 'EN', fr: 'FR', es: 'ES' };

const BRANDS = [
  { value: 'yulontap', label: 'YULONTAP' },
  { value: 'tapelligence', label: 'Tapelligence' },
];

const normI18n = (v) => {
  if (!v) return { en: '', fr: '', es: '' };
  if (typeof v === 'string') return { en: v, fr: '', es: '' };
  return { en: v.en || '', fr: v.fr || '', es: v.es || '' };
};

const displayI18n = (v, lang = 'en') => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return v[lang] || v.en || Object.values(v).find(x => x) || '';
};

const getVerticalKey = (vertical) => {
  const v = (vertical || '').toLowerCase();
  if (v.includes('hotel') || v.includes('hospitality') || v.includes('resort')) return 'Hotel';
  if (v.includes('restaurant') || v.includes('qsr') || v.includes('dining') || v.includes('food')) return 'Restaurant';
  if (v.includes('medical') || v.includes('health') || v.includes('clinic') || v.includes('hospital')) return 'Medical';
  if (v.includes('retail') || v.includes('store') || v.includes('warehouse')) return 'Retail';
  if (v.includes('casino') || v.includes('gaming')) return 'Casino';
  return 'Default';
};

const resolveImgSrc = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

// ── Language Input ──────────────────────────────
const LangInput = ({ value, onChange, placeholder, testId, className = '' }) => {
  const [activeLang, setActiveLang] = useState('en');
  const obj = normI18n(value);
  return (
    <div className={className}>
      <div className="flex gap-0.5 mb-1">
        {LANGS.map(l => (
          <button key={l} type="button" onClick={() => setActiveLang(l)}
            className={`px-2 py-0.5 text-[9px] font-bold rounded-md transition-colors ${activeLang === l ? 'bg-[#0077FF] text-white' : 'bg-slate-100 text-slate-400 hover:text-slate-600'}${!obj[l] && l !== 'en' ? ' opacity-60' : ''}`}
            data-testid={testId ? `${testId}-lang-${l}` : undefined}>{LANG_LABELS[l]}</button>
        ))}
      </div>
      <input value={obj[activeLang] || ''} onChange={e => onChange({ ...obj, [activeLang]: e.target.value })} placeholder={placeholder}
        onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0077FF]/20 focus:border-[#0077FF]/40 transition-all"
        data-testid={testId ? `${testId}-${activeLang}` : undefined} />
    </div>
  );
};

// ── Step Indicator ──────────────────────────────
const StepIndicator = ({ step, t }) => {
  const phases = [
    { label: t('admin.guide.step1Title'), steps: [0] },
    { label: t('admin.guide.phaseBuild'), steps: [1, 2, 3, 4, 5] },
    { label: t('admin.guide.step7Title'), steps: [6] },
    { label: t('admin.guide.step8Title'), steps: [7] },
  ];
  const activePhase = phases.findIndex(p => p.steps.includes(step));

  return (
    <div className="flex items-center gap-1.5" data-testid="guide-step-indicator">
      {phases.map((phase, i) => (
        <div key={i} className="flex items-center gap-1.5">
          {i > 0 && <div className={`w-6 h-px ${i <= activePhase ? 'bg-[#0077FF]' : 'bg-slate-200'}`} />}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors ${
            i === activePhase ? 'bg-[#0077FF]/10 text-[#0077FF] ring-1 ring-[#0077FF]/20' :
            i < activePhase ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
          }`}>
            {i < activePhase ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
            <span className="hidden sm:inline">{phase.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Progress Summary ────────────────────────────
const ProgressSummary = ({ form, t, language }) => {
  const totalCards = form.scenes.reduce((n, s) => n + (s.cards?.length || 0), 0);
  const capIds = new Set();
  form.scenes.forEach(s => (s.cards || []).forEach(c => { if (c.capability_id) capIds.add(c.capability_id); }));
  const name = displayI18n(form.name, language);
  if (!name && form.scenes.length === 0) return null;

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-slate-50/80 border border-slate-200 rounded-xl text-xs flex-wrap" data-testid="guide-progress-summary">
      {name && <span className="font-semibold text-slate-700 truncate max-w-[180px]">{name}</span>}
      {name && <span className="text-slate-300">|</span>}
      <span className="text-slate-500">{form.scenes.length} {t('admin.guide.progressScenes')}</span>
      <span className="text-slate-300">|</span>
      <span className="text-slate-500">{totalCards} {t('admin.guide.progressCards')}</span>
      <span className="text-slate-300">|</span>
      <span className="text-slate-500">{capIds.size} {t('admin.guide.progressCaps')}</span>
    </div>
  );
};

// ── Guidance Box ────────────────────────────────
const GuidanceBox = ({ children, variant = 'info' }) => {
  const styles = variant === 'tip'
    ? 'bg-amber-50/60 border-amber-100 text-amber-700'
    : 'bg-blue-50/60 border-blue-100 text-blue-800';
  return <div className={`border rounded-xl p-4 ${styles}`}>{children}</div>;
};

const GUIDE_STORAGE_KEY = 'tapelligence_guide_form';

const saveGuideState = (data) => {
  try { sessionStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(data)); } catch (_) { /* ignore */ }
};
const loadGuideState = () => {
  try {
    const raw = sessionStorage.getItem(GUIDE_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  return null;
};
const clearGuideState = () => {
  try { sessionStorage.removeItem(GUIDE_STORAGE_KEY); } catch (_) { /* ignore */ }
};

// ══════════════════════════════════════════════════
// ── Main Guided Setup Component ─────────────────
// ══════════════════════════════════════════════════
const GuidedSetup = ({ experience, capabilities, onSave, onCancel, onSkip, saving, t, language }) => {
  const saved = loadGuideState();

  const [step, setStep] = useState(() => saved?.step ?? 0);
  const [sceneIdx, setSceneIdx] = useState(() => saved?.sceneIdx ?? 0);
  const [activeCardIdx, setActiveCardIdx] = useState(() => saved?.activeCardIdx ?? 0);

  const [form, setForm] = useState(() => {
    // Restore from sessionStorage if available
    if (saved?.form) return saved.form;

    if (experience?.id) {
      return {
        ...experience,
        name: normI18n(experience.name),
        mode_labels: Object.fromEntries(
          Object.entries(experience.mode_labels || {}).map(([k, v]) => [k, normI18n(v)])
        ),
        scenes: (experience.scenes || []).map(s => ({
          ...s,
          name: normI18n(s.name),
          cards: (s.cards || []).map(c => ({ ...c, label: normI18n(c.label) })),
        })),
      };
    }
    return {
      name: { en: '', fr: '', es: '' },
      slug: '',
      brand: 'yulontap',
      vertical: '',
      status: 'draft',
      available_modes: ['standard'],
      mode_labels: { standard: { en: 'Standard', fr: 'Standard', es: 'Estándar' } },
      scenes: [],
    };
  });

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const currentScene = form.scenes[sceneIdx];
  const vKey = getVerticalKey(form.vertical);

  // Persist guide state to sessionStorage on every change
  useEffect(() => {
    saveGuideState({ form, step, sceneIdx, activeCardIdx });
  }, [form, step, sceneIdx, activeCardIdx]);

  const groupedCaps = useMemo(() => {
    const groups = {};
    (capabilities || []).forEach(cap => {
      const cat = cap.category || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(cap);
    });
    return groups;
  }, [capabilities]);

  const capInstanceCounts = useMemo(() => {
    const counts = {};
    (currentScene?.cards || []).forEach(c => { if (c.capability_id) counts[c.capability_id] = (counts[c.capability_id] || 0) + 1; });
    return counts;
  }, [currentScene]);

  // ── Form Mutations ─────────────────────────────
  const updateScene = (field, value) => {
    setForm(prev => {
      const scenes = [...prev.scenes];
      scenes[sceneIdx] = { ...scenes[sceneIdx], [field]: value };
      return { ...prev, scenes };
    });
  };

  const updateCard = (cardIdx, updates) => {
    setForm(prev => {
      const scenes = [...prev.scenes];
      const cards = [...(scenes[sceneIdx].cards || [])];
      cards[cardIdx] = { ...cards[cardIdx], ...updates };
      scenes[sceneIdx] = { ...scenes[sceneIdx], cards };
      return { ...prev, scenes };
    });
  };

  const addCardFromCapability = (cap) => {
    setForm(prev => {
      const scenes = [...prev.scenes];
      const cards = [...(scenes[sceneIdx].cards || [])];
      const newDests = {};
      if (cap?.default_destination_url) {
        (prev.available_modes || ['standard']).forEach(m => { newDests[m] = cap.default_destination_url; });
      }
      cards.push({
        label: normI18n(cap ? cap.name : 'New Card'),
        position_x: 50, position_y: 50, card_scale: 1,
        card_image_url: cap?.default_image_url || '',
        capability_id: cap?.id || '',
        destinations: newDests,
      });
      scenes[sceneIdx] = { ...scenes[sceneIdx], cards };
      return { ...prev, scenes };
    });
    setActiveCardIdx(currentScene?.cards?.length || 0);
  };

  const removeCard = (cardIdx) => {
    setForm(prev => {
      const scenes = [...prev.scenes];
      const cards = (scenes[sceneIdx].cards || []).filter((_, i) => i !== cardIdx);
      scenes[sceneIdx] = { ...scenes[sceneIdx], cards };
      return { ...prev, scenes };
    });
    setActiveCardIdx(Math.max(0, activeCardIdx - 1));
  };

  const updateCardPosition = (cardIdx, x, y) => updateCard(cardIdx, { position_x: x, position_y: y });

  const updateCardDest = (cardIdx, key, url) => {
    setForm(prev => {
      const scenes = [...prev.scenes];
      const cards = [...(scenes[sceneIdx].cards || [])];
      cards[cardIdx] = { ...cards[cardIdx], destinations: { ...(cards[cardIdx].destinations || {}), [key]: url } };
      scenes[sceneIdx] = { ...scenes[sceneIdx], cards };
      return { ...prev, scenes };
    });
  };

  const removeCardDest = (cardIdx, key) => {
    setForm(prev => {
      const scenes = [...prev.scenes];
      const cards = [...(scenes[sceneIdx].cards || [])];
      const dests = { ...(cards[cardIdx].destinations || {}) };
      delete dests[key];
      cards[cardIdx] = { ...cards[cardIdx], destinations: dests };
      scenes[sceneIdx] = { ...scenes[sceneIdx], cards };
      return { ...prev, scenes };
    });
  };

  const addCardAction = (cardIdx) => {
    const name = prompt(t('admin.guide.actionNamePrompt') || 'Action name (e.g., Welcome, Wi-Fi, Gift):');
    if (!name?.trim()) return;
    const key = name.trim().toLowerCase().replace(/\s+/g, '_');
    updateCardDest(cardIdx, key, '');
  };

  // ── Navigation ─────────────────────────────────
  const goNext = () => {
    if (step === 0 && form.scenes.length === 0) {
      setForm(prev => ({
        ...prev,
        scenes: [{ name: { en: '', fr: '', es: '' }, background_url: '', cards: [] }]
      }));
      setSceneIdx(0);
    }
    setStep(s => Math.min(s + 1, 7));
  };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const addAnotherScene = () => {
    setForm(prev => ({
      ...prev,
      scenes: [...prev.scenes, { name: { en: '', fr: '', es: '' }, background_url: '', cards: [] }]
    }));
    setSceneIdx(form.scenes.length);
    setActiveCardIdx(0);
    setStep(1);
  };

  const handlePreview = async () => {
    try {
      let id = form.id;
      if (!id) {
        const saved = await onSave({ ...form, status: 'draft' }, true);
        if (saved?.id) { id = saved.id; setForm(prev => ({ ...prev, id: saved.id })); }
      } else {
        await onSave(form, true);
      }
      if (id) window.open(`/experience/admin-preview/${id}?lang=${language}`, '_blank');
    } catch (e) { /* preview error silenced */ }
  };

  const handleSaveDraft = () => { clearGuideState(); onSave({ ...form, status: 'draft' }); };
  const handleActivate = () => { clearGuideState(); onSave({ ...form, status: 'active' }); };

  const canProceed = () => {
    if (step === 0) return !!(normI18n(form.name).en);
    if (step === 1) return !!(normI18n(currentScene?.name).en);
    return true;
  };

  const getNextLabel = () => {
    switch (step) {
      case 0: return t('admin.guide.btnCreateScene');
      case 1: return t('admin.guide.btnChooseCaps');
      case 2: return t('admin.guide.btnPositionCards');
      case 3: return t('admin.guide.btnConfigDests');
      case 4: return t('admin.guide.btnContinue');
      default: return t('admin.guide.next');
    }
  };

  // ── Step Renderers ─────────────────────────────

  const renderDetails = () => (
    <div className="space-y-5" data-testid="guide-step-details">
      <GuidanceBox>
        <p className="text-sm leading-relaxed">{t('admin.guide.detailsGuidance')}</p>
      </GuidanceBox>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{t('admin.experiences.name')}</label>
        <LangInput value={form.name} onChange={v => updateField('name', v)} placeholder={t('admin.guide.namePlaceholder')} testId="guide-name" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{t('admin.experiences.brand')}</label>
          <select value={form.brand} onChange={e => updateField('brand', e.target.value)} data-testid="guide-brand"
            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0077FF]/20">
            {BRANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">{t('admin.experiences.vertical')}</label>
          <input value={form.vertical} onChange={e => updateField('vertical', e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
            placeholder={t('admin.guide.verticalPlaceholder')}
            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0077FF]/20" data-testid="guide-vertical" />
        </div>
      </div>

      {form.vertical && (
        <GuidanceBox variant="tip">
          <p className="text-xs leading-relaxed">
            <span className="font-semibold">{t('admin.guide.verticalTip')}</span>{' '}
            {t(`admin.guide.hint${vKey}Scenes`)}
          </p>
        </GuidanceBox>
      )}
    </div>
  );

  const renderSceneSetup = () => (
    <div className="space-y-5" data-testid="guide-step-scene">
      <GuidanceBox>
        <p className="text-sm leading-relaxed">{t('admin.guide.sceneGuidance')}</p>
        {form.vertical && (
          <p className="text-xs mt-2 opacity-80">{t(`admin.guide.hint${vKey}Scenes`)}</p>
        )}
      </GuidanceBox>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
          {t('admin.experiences.sceneName')} ({t('admin.guide.step')} {sceneIdx + 1})
        </label>
        <LangInput value={currentScene?.name} onChange={v => updateScene('name', v)}
          placeholder={t('admin.guide.sceneNamePlaceholder')} testId="guide-scene-name" />
      </div>

      <div>
        <ImageUpload value={currentScene?.background_url || ''} onChange={v => updateScene('background_url', v)}
          label={t('admin.experiences.bgImage')} testId="guide-scene-bg" />
      </div>
    </div>
  );

  const renderCardSelection = () => {
    const cards = currentScene?.cards || [];
    const catEntries = Object.entries(groupedCaps);

    return (
      <div className="space-y-5" data-testid="guide-step-cards">
        <GuidanceBox>
          <p className="text-sm font-semibold mb-1">{t('admin.guide.whatToDemonstrate')}</p>
          <p className="text-sm leading-relaxed">
            {form.vertical ? t(`admin.guide.hint${vKey}Caps`) : t('admin.guide.defaultCapsHint')}
          </p>
        </GuidanceBox>

        {/* Selected cards in scene */}
        {cards.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{cards.length} {t('admin.guide.cardsInScene')}</p>
            {cards.map((card, i) => (
              <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
                i === activeCardIdx ? 'border-[#0077FF]/30 bg-blue-50/50' : 'border-slate-200 bg-white'
              }`} onClick={() => setActiveCardIdx(i)}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {card.card_image_url && (
                    <img src={resolveImgSrc(card.card_image_url)} alt="" className="w-6 h-6 rounded object-contain shrink-0" />
                  )}
                  {i === activeCardIdx ? (
                    <input
                      value={displayI18n(card.label, language)}
                      onChange={e => updateCard(i, { label: { ...normI18n(card.label), [language]: e.target.value } })}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                      className="text-sm text-slate-700 bg-white/80 border border-slate-200 rounded-md px-2 py-0.5 outline-none focus:ring-1 focus:ring-[#0077FF]/30 flex-1 min-w-0"
                      placeholder="e.g. Bathroom Monitor — Floor 1"
                      data-testid={`card-instance-label-${i}`}
                    />
                  ) : (
                    <span className="text-sm text-slate-700 truncate cursor-pointer">{displayI18n(card.label, language) || `Card ${i + 1}`}</span>
                  )}
                </div>
                <button type="button" onClick={e => { e.stopPropagation(); removeCard(i); }} className="text-red-400 hover:text-red-600 p-1 shrink-0" data-testid={`guide-remove-card-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Capability Library grouped by category */}
        {catEntries.length > 0 ? (
          <div className="space-y-5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('admin.guide.selectCap')}</p>
            {catEntries.map(([cat, caps]) => (
              <div key={cat} data-testid={`guide-cap-group-${cat}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <LayoutGrid className="w-3.5 h-3.5 text-slate-400" />
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">{cat}</p>
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-[10px] text-slate-400">{caps.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {caps.map(cap => {
                    const instanceCount = capInstanceCounts[cap.id] || 0;
                    const capName = typeof cap.name === 'object' ? displayI18n(cap.name, language) : cap.name;
                    return (
                      <button type="button" key={cap.id} onClick={() => addCardFromCapability(cap)}
                        data-testid={`guide-cap-${cap.id}`}
                        className={`flex items-center gap-3 text-left px-3 py-3 rounded-xl border transition-all ${
                          instanceCount > 0
                            ? 'border-emerald-200 bg-emerald-50/30 hover:border-[#0077FF]/30 hover:bg-blue-50/30 cursor-pointer'
                            : 'border-slate-200 hover:border-[#0077FF]/30 hover:bg-blue-50/30 cursor-pointer'
                        }`}>
                        {cap.default_image_url ? (
                          <img src={resolveImgSrc(cap.default_image_url)} alt=""
                            className="w-10 h-10 rounded-lg object-contain bg-white border border-slate-100 shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            <Layers className="w-4 h-4 text-slate-300" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-slate-700">{capName}</p>
                          {instanceCount > 0 && (
                            <p className="text-[10px] text-emerald-500 font-medium">{instanceCount} in scene — tap to add another</p>
                          )}
                        </div>
                        {instanceCount > 0 && (
                          <span className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[10px] font-bold">{instanceCount}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{t('admin.guide.noCapsAvailable')}</p>
          </div>
        )}

        {/* Custom card */}
        <button type="button" onClick={() => addCardFromCapability(null)} data-testid="guide-add-custom-card"
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-[#0077FF] hover:bg-blue-50 rounded-xl border border-dashed border-[#0077FF]/30 transition-colors w-full justify-center">
          <Plus className="w-4 h-4" /> {t('admin.guide.customCard')}
        </button>

        {/* Card detail editing */}
        {cards.length > 0 && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              {displayI18n(cards[activeCardIdx]?.label, 'en') || `Card ${activeCardIdx + 1}`}
            </p>
            <LangInput value={cards[activeCardIdx]?.label} onChange={v => updateCard(activeCardIdx, { label: v })}
              placeholder={t('admin.experiences.cardLabel')} testId="guide-card-label" />
            <ImageUpload value={cards[activeCardIdx]?.card_image_url || ''} onChange={v => updateCard(activeCardIdx, { card_image_url: v })}
              label={t('admin.experiences.cardImage') || 'Card Image'} testId="guide-card-image" />
          </div>
        )}
      </div>
    );
  };

  const renderCardPosition = () => {
    const cards = currentScene?.cards || [];
    if (cards.length === 0) return (
      <div className="text-center py-12 text-slate-400 text-sm" data-testid="guide-step-position">{t('admin.guide.noCardsYet')}</div>
    );
    return (
      <div className="space-y-4" data-testid="guide-step-position">
        <GuidanceBox>
          <p className="text-xs leading-relaxed">
            {form.vertical ? t(`admin.guide.hint${vKey}Position`) : t('admin.guide.defaultPositionHint')}
          </p>
        </GuidanceBox>

        {cards.length > 1 && (
          <div className="flex gap-1.5 flex-wrap">
            {cards.map((c, i) => (
              <button type="button" key={i} onClick={() => setActiveCardIdx(i)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  i === activeCardIdx ? 'bg-[#0077FF] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}>
                {displayI18n(c.label, 'en') || `Card ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        <CardPositioner bgUrl={currentScene?.background_url} cards={cards} activeCardIndex={activeCardIdx} onUpdatePosition={updateCardPosition} onSelectCard={setActiveCardIdx} placeholder={t('admin.guide.step4Hint')} />

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold text-slate-400">{t('admin.guide.positionX')}</label>
            <input type="number" value={cards[activeCardIdx]?.position_x ?? 50}
              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
              onChange={e => updateCardPosition(activeCardIdx, parseFloat(e.target.value) || 0, cards[activeCardIdx]?.position_y ?? 50)}
              className="w-20 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-center" data-testid="guide-pos-x" />
            <span className="text-[10px] text-slate-400">%</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold text-slate-400">{t('admin.guide.positionY')}</label>
            <input type="number" value={cards[activeCardIdx]?.position_y ?? 50}
              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
              onChange={e => updateCardPosition(activeCardIdx, cards[activeCardIdx]?.position_x ?? 50, parseFloat(e.target.value) || 0)}
              className="w-20 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-center" data-testid="guide-pos-y" />
            <span className="text-[10px] text-slate-400">%</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold text-slate-400">Scale</label>
            <input type="range" min="0.5" max="3" step="0.1" value={cards[activeCardIdx]?.card_scale ?? 1}
              onChange={e => updateCard(activeCardIdx, { card_scale: parseFloat(e.target.value) })}
              className="w-24 h-1.5 accent-[#0077FF]" data-testid="guide-card-scale" />
            <span className="text-xs text-slate-500 font-mono w-8">{(cards[activeCardIdx]?.card_scale ?? 1).toFixed(1)}x</span>
          </div>
        </div>
      </div>
    );
  };

  const renderDestinations = () => {
    const cards = currentScene?.cards || [];
    if (cards.length === 0) return (
      <div className="text-center py-12 text-slate-400 text-sm" data-testid="guide-step-destinations">{t('admin.guide.noCardsYet')}</div>
    );

    return (
      <div className="space-y-4" data-testid="guide-step-destinations">
        <GuidanceBox>
          <p className="text-xs leading-relaxed">
            {form.vertical ? t(`admin.guide.hint${vKey}Dests`) : t('admin.guide.defaultDestsHint')}
          </p>
        </GuidanceBox>

        {cards.map((card, ci) => {
          const destEntries = Object.entries(card.destinations || {});
          return (
            <div key={ci} className="border border-slate-200 rounded-xl p-4 space-y-2.5">
              <div className="flex items-center gap-2">
                {card.card_image_url && (
                  <img src={resolveImgSrc(card.card_image_url)} alt="" className="w-6 h-6 rounded object-contain shrink-0" />
                )}
                <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5 text-slate-400" />
                  {displayI18n(card.label, language) || `Card ${ci + 1}`}
                </p>
              </div>
              {destEntries.length === 0 && (
                <p className="text-[10px] text-slate-400 italic">{t('admin.guide.noActionsYet') || 'No actions yet — add one below'}</p>
              )}
              {destEntries.map(([key, url]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-600 font-semibold w-24 shrink-0 truncate capitalize bg-slate-50 px-2 py-1.5 border border-slate-100 rounded-lg">
                    {key.replace(/[_-]/g, ' ')}
                  </span>
                  <input value={url} onChange={e => updateCardDest(ci, key, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                    placeholder={t('admin.guide.destPlaceholder')}
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#0077FF]/20"
                    data-testid={`guide-dest-${ci}-${key}`} />
                  <button type="button" onClick={() => removeCardDest(ci, key)}
                    className="p-1 text-red-400 hover:text-red-600 rounded shrink-0" title="Remove">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addCardAction(ci)}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-[#0077FF] hover:bg-blue-50 rounded-lg transition-colors"
                data-testid={`guide-add-action-${ci}`}>
                <Plus className="w-3 h-3" /> {t('admin.guide.addAction') || 'Add Action'}
              </button>
              {card.capability_id && destEntries.some(([, u]) => u) && (
                <p className="text-[10px] text-emerald-500 flex items-center gap-1">
                  <Check className="w-3 h-3" /> {t('admin.guide.inheritedFromCap')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMoreScenes = () => (
    <div className="text-center py-6 space-y-6" data-testid="guide-step-more-scenes">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-600 text-sm font-semibold">
        <Check className="w-4 h-4" /> {form.scenes.length} {t('admin.guide.sceneCount')}
      </div>
      <div className="space-y-3 max-w-sm mx-auto">
        {form.scenes.map((s, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 rounded-xl text-left">
            <div className="w-10 h-10 rounded-lg bg-slate-200 overflow-hidden shrink-0">
              {s.background_url && <img src={s.background_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">{displayI18n(s.name, language) || `Scene ${i + 1}`}</p>
              <p className="text-[10px] text-slate-400">{s.cards?.length || 0} {t('admin.guide.cardsInScene')}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3 max-w-xs mx-auto">
        <button type="button" onClick={addAnotherScene} data-testid="guide-add-scene"
          className="w-full px-5 py-3 bg-white border-2 border-dashed border-[#0077FF]/30 text-[#0077FF] text-sm font-semibold rounded-xl hover:bg-blue-50/50 transition-colors">
          <Plus className="w-4 h-4 inline mr-2" />{t('admin.guide.step6Yes')}
        </button>
        <button type="button" onClick={() => setStep(6)} data-testid="guide-continue-preview"
          className="w-full px-5 py-3 bg-[#0077FF] text-white text-sm font-semibold rounded-xl hover:bg-[#0066DD] transition-colors">
          {t('admin.guide.step6No')} <ChevronRight className="w-4 h-4 inline ml-1" />
        </button>
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="text-center py-8 space-y-6" data-testid="guide-step-preview">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-[#0077FF]/10 flex items-center justify-center">
        <Eye className="w-7 h-7 text-[#0077FF]" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-800">{displayI18n(form.name, language)}</h3>
        <p className="text-sm text-slate-400 mt-1">
          {form.scenes.length} {t('admin.guide.sceneCount')} &middot; {form.scenes.reduce((n, s) => n + (s.cards?.length || 0), 0)} {t('admin.guide.progressCards')}
        </p>
      </div>
      <GuidanceBox>
        <p className="text-xs leading-relaxed">{t('admin.guide.previewGuidance')}</p>
      </GuidanceBox>
      <button type="button" onClick={handlePreview} data-testid="guide-open-preview"
        className="inline-flex items-center gap-2 px-6 py-3 bg-[#0077FF] text-white text-sm font-semibold rounded-xl hover:bg-[#0066DD] transition-colors shadow-lg shadow-[#0077FF]/20">
        <Eye className="w-4 h-4" /> {t('admin.guide.step7Open')}
      </button>
    </div>
  );

  const renderSaveActivate = () => (
    <div className="text-center py-8 space-y-6" data-testid="guide-step-save">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
        <Zap className="w-7 h-7 text-emerald-500" />
      </div>
      <p className="text-sm text-slate-500">{t('admin.guide.step8Desc')}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button type="button" onClick={handleSaveDraft} disabled={saving} data-testid="guide-save-draft"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors">
          <Save className="w-4 h-4" /> {t('admin.guide.saveDraft')}
        </button>
        <button type="button" onClick={handlePreview} data-testid="guide-preview-btn"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-[#0077FF]/30 text-[#0077FF] text-sm font-semibold rounded-xl hover:bg-blue-50 transition-colors">
          <Eye className="w-4 h-4" /> {t('admin.guide.preview')}
        </button>
        <button type="button" onClick={handleActivate} disabled={saving} data-testid="guide-activate"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0077FF] text-white text-sm font-semibold rounded-xl hover:bg-[#0066DD] transition-colors shadow-lg shadow-[#0077FF]/20">
          <Zap className="w-4 h-4" /> {t('admin.guide.activate')}
        </button>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return renderDetails();
      case 1: return renderSceneSetup();
      case 2: return renderCardSelection();
      case 3: return renderCardPosition();
      case 4: return renderDestinations();
      case 5: return renderMoreScenes();
      case 6: return renderPreview();
      case 7: return renderSaveActivate();
      default: return null;
    }
  };

  const stepTitle = t(`admin.guide.step${step + 1}Title`);
  const stepDesc = t(`admin.guide.step${step + 1}Desc`);
  const sceneLabel = step >= 1 && step <= 5 ? ` — ${displayI18n(currentScene?.name, language) || `Scene ${sceneIdx + 1}`}` : '';

  return (
    <div className="space-y-4" data-testid="guided-setup">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => { clearGuideState(); onCancel(); }} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="guide-exit-btn">
            <X className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">{t('admin.guide.title')}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{experience?.id ? t('admin.experiences.edit') : t('admin.experiences.new')}</p>
          </div>
        </div>
        <button type="button" onClick={() => onSkip(form)} data-testid="guide-skip-btn"
          className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors">
          {t('admin.guide.skip')}
        </button>
      </div>

      {/* Phase Indicator */}
      <StepIndicator step={step} t={t} />

      {/* Live Progress */}
      <ProgressSummary form={form} t={t} language={language} />

      {/* Step Content */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="mb-5">
          <h2 className="text-base font-bold text-slate-800">{stepTitle}{sceneLabel}</h2>
          <p className="text-sm text-slate-400 mt-0.5">{stepDesc}</p>
        </div>
        {renderStep()}
      </div>

      {/* Navigation */}
      {step !== 5 && step !== 7 && (
        <div className="flex items-center justify-between">
          <button type="button" onClick={goBack} disabled={step === 0} data-testid="guide-back-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl transition-colors">
            <ChevronLeft className="w-4 h-4" /> {t('admin.guide.back')}
          </button>
          <button type="button" onClick={goNext} disabled={!canProceed()} data-testid="guide-next-btn"
            className="flex items-center gap-1.5 px-5 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl transition-colors">
            {getNextLabel()} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default GuidedSetup;
